import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DestroyRef, Directive, ElementRef, effect, inject, input, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Directive({
  selector: '[cipoKeyboardList]',
  standalone: true,
  host: {
    tabindex: '0',
    '[attr.aria-label]': 'ariaLabel()',
    '(keydown)': 'onKeydown($event)',
  },
})
export class CipoKeyboardListDirective {
  private viewport = inject(CdkVirtualScrollViewport, { optional: true, self: true });
  private el = inject(ElementRef<HTMLElement>);
  private destroyRef = inject(DestroyRef);

  ariaLabel = input<string>('');
  itemCount = input<number>();
  itemSelector = input<string>('tr[tabindex]');

  private currentIndex = -1;
  private pendingFocusIndex = -1;
  private prevItemCount = 0;

  constructor() {
    if (!this.viewport) return;

    effect(() => {
      const newCount = this.itemCount() ?? 0;
      const prev = this.prevItemCount;
      this.prevItemCount = newCount;
      if (newCount > prev && prev > 0 && this.currentIndex === prev - 1) {
        untracked(() => this.navigate(this.currentIndex + 1));
      }
    });

    this.viewport.renderedRangeStream.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(range => {
      if (this.pendingFocusIndex < 0) return;
      const pendingIdx = this.pendingFocusIndex;
      requestAnimationFrame(() => {
        if (this.pendingFocusIndex !== pendingIdx) return;
        const item = this.el.nativeElement.querySelector(`[data-list-index="${pendingIdx}"]`) as HTMLElement;
        if (item) {
          item.focus({ preventScroll: true });
          this.pendingFocusIndex = -1;
        } else if (pendingIdx < range.start || pendingIdx >= range.end) {
          this.scrollToEstimated(pendingIdx);
        }
      });
    });
  }

  onKeydown(event: KeyboardEvent): void {
    const total = this.viewport
      ? (this.itemCount() ?? 0)
      : this.el.nativeElement.querySelectorAll(this.itemSelector()).length;

    if (total === 0) return;

    const activeEl = document.activeElement as HTMLElement;
    // Virtual scroll rows are identified by data-list-index; paged rows by itemSelector (tr[tabindex]).
    const rowEl = activeEl?.closest<HTMLElement>(this.viewport ? '[data-list-index]' : this.itemSelector()) ?? null;
    const isOnRow = rowEl === activeEl;

    // Tab only intercepts when focus is on/inside a data row — viewport and sortable headers
    // are skipped naturally by the browser (Tab navigates them without interception).
    if (event.key === 'Tab' && this.viewport && rowEl !== null) {
      const target = this.findTabTarget(event.shiftKey);
      if (target) {
        event.preventDefault();
        target.focus();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!isOnRow && rowEl) {
          this.currentIndex = this.rowIndex(rowEl);
        }
        if (this.currentIndex < total - 1) {
          this.navigate(this.currentIndex + 1);
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (!isOnRow && rowEl) {
          this.currentIndex = this.rowIndex(rowEl);
        }
        if (this.currentIndex > 0) {
          this.navigate(this.currentIndex - 1);
        }
        break;

      case 'ArrowRight':
        event.preventDefault();
        if (isOnRow && rowEl) {
          this.getRowCells(rowEl)[0]?.focus();
        } else if (rowEl) {
          const cells = this.getRowCells(rowEl);
          const idx = cells.indexOf(activeEl);
          if (idx >= 0 && idx < cells.length - 1) cells[idx + 1].focus();
        }
        break;

      case 'ArrowLeft':
        event.preventDefault();
        if (isOnRow && rowEl) {
          const cells = this.getRowCells(rowEl);
          cells[cells.length - 1]?.focus();
        } else if (rowEl) {
          const cells = this.getRowCells(rowEl);
          const idx = cells.indexOf(activeEl);
          if (idx > 0) {
            cells[idx - 1].focus();
          } else {
            this.currentIndex = this.rowIndex(rowEl);
            rowEl.focus({ preventScroll: true });
          }
        }
        break;

      case 'Escape':
        if (isOnRow || !rowEl) break;
        event.preventDefault();
        this.currentIndex = this.rowIndex(rowEl);
        rowEl.focus({ preventScroll: true });
        break;
    }
  }

  private getRowCells(row: HTMLElement): HTMLElement[] {
    return Array.from(
      row.querySelectorAll<HTMLElement>('input[type="checkbox"]:not([disabled]), button:not([disabled])'),
    );
  }

  private rowIndex(row: HTMLElement): number {
    const attr = row.getAttribute('data-list-index');
    if (attr !== null) return parseInt(attr, 10);
    const rows = Array.from(this.el.nativeElement.querySelectorAll(this.itemSelector())) as HTMLElement[];
    return rows.indexOf(row);
  }

  // Returns the nearest tabbable element before/after the viewport that is not inside it.
  // Uses compareDocumentPosition — no need to locate the viewport in the tabbable list.
  private findTabTarget(reverse: boolean): HTMLElement {
    const tabbable = Array.from(
      document.querySelectorAll<HTMLElement>(
        'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), ' +
          'input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), ' +
          'textarea:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])',
      ),
    );

    const viewportEl = this.el.nativeElement;

    if (reverse) {
      for (let i = tabbable.length - 1; i >= 0; i--) {
        const pos = viewportEl.compareDocumentPosition(tabbable[i]);
        if (pos & 2 && !(pos & 8) && this.canReceiveFocus(tabbable[i])) return tabbable[i];
      }
    } else {
      for (const el of tabbable) {
        const pos = viewportEl.compareDocumentPosition(el);
        if (pos & 4 && !(pos & 16) && this.canReceiveFocus(el)) return el;
      }
    }
    return null;
  }

  private navigate(index: number): void {
    this.currentIndex = index;

    if (this.viewport) {
      const item = this.el.nativeElement.querySelector(`[data-list-index="${index}"]`) as HTMLElement;

      if (item) {
        const vpRect = this.el.nativeElement.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();
        const { top: stickyTop, bottom: stickyBottom } = this.getStickyBounds(vpRect);
        const hiddenBelow = itemRect.bottom - (vpRect.bottom - stickyBottom);
        const hiddenAbove = (vpRect.top + stickyTop) - itemRect.top;

        if (hiddenBelow > 0 || hiddenAbove > 0) {
          this.pendingFocusIndex = index;
          const delta = hiddenBelow > 0 ? hiddenBelow : -hiddenAbove;
          this.viewport.scrollToOffset(this.viewport.measureScrollOffset() + delta, 'instant');
          requestAnimationFrame(() => {
            if (this.pendingFocusIndex !== index) return;
            const afterScrollItem = this.el.nativeElement.querySelector(`[data-list-index="${index}"]`) as HTMLElement;
            if (afterScrollItem) {
              afterScrollItem.focus({ preventScroll: true });
              this.pendingFocusIndex = -1;
            }
          });
        } else {
          item.focus({ preventScroll: true });
        }
      } else {
        const range = this.viewport.getRenderedRange();
        this.pendingFocusIndex = index;

        if (index >= range.start && index < range.end) {
          // CDK says item is rendered but DOM binding not applied yet (recycleRows timing).
          // Retry after one frame before falling back to a scroll.
          requestAnimationFrame(() => {
            if (this.pendingFocusIndex !== index) return;
            const retryItem = this.el.nativeElement.querySelector(`[data-list-index="${index}"]`) as HTMLElement;
            if (retryItem) {
              retryItem.focus({ preventScroll: true });
              this.pendingFocusIndex = -1;
            } else {
              this.scrollToEstimated(index);
            }
          });
        } else {
          this.scrollToEstimated(index);
        }
      }
    } else {
      const item = this.el.nativeElement.querySelectorAll(this.itemSelector())[index] as HTMLElement;
      if (!item) return;
      const vpRect = this.el.nativeElement.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const { top: stickyTop, bottom: stickyBottom } = this.getStickyBounds(vpRect);
      const hiddenBelow = itemRect.bottom - (vpRect.bottom - stickyBottom);
      const hiddenAbove = (vpRect.top + stickyTop) - itemRect.top;
      if (hiddenBelow > 0 || hiddenAbove > 0) {
        this.el.nativeElement.scrollTop += hiddenBelow > 0 ? hiddenBelow : -hiddenAbove;
      }
      item.focus({ preventScroll: true });
    }
  }

  private canReceiveFocus(el: HTMLElement): boolean {
    let node: HTMLElement = el;
    while (node) {
      if (node.hasAttribute('inert') || node.getAttribute('aria-hidden') === 'true') return false;
      node = node.parentElement;
    }
    return window.getComputedStyle(el).visibility !== 'hidden';
  }

  private getStickyBounds(vpRect: DOMRect): { top: number; bottom: number } {
    const el = this.el.nativeElement as HTMLElement;
    let top = 0, bottom = 0;
    // CDK Table applies position:sticky to <th>/<td> cells, not to <thead>/<tfoot>/<tr> containers.
    // Since the cells are sticky they stay at their visual position regardless of scroll offset.
    const firstHeaderCell = el.querySelector<HTMLElement>('thead th, thead td');
    if (firstHeaderCell && window.getComputedStyle(firstHeaderCell).position === 'sticky') {
      const b = firstHeaderCell.getBoundingClientRect().bottom;
      if (b > vpRect.top) top = b - vpRect.top;
    }
    const firstFooterCell = el.querySelector<HTMLElement>('tfoot td, tfoot th');
    if (firstFooterCell && window.getComputedStyle(firstFooterCell).position === 'sticky') {
      const t = firstFooterCell.getBoundingClientRect().top;
      if (t < vpRect.bottom) bottom = vpRect.bottom - t;
    }
    return { top, bottom };
  }

  private scrollToEstimated(index: number): void {
    const range = this.viewport!.getRenderedRange();
    const scrollOffset = this.viewport!.measureScrollOffset();
    const viewportSize = this.viewport!.getViewportSize();

    let targetOffset: number;
    if (range.start > 0 && scrollOffset > 0) {
      const avgItemHeight = scrollOffset / range.start;
      targetOffset = Math.max(0, index * avgItemHeight - viewportSize / 2);
    } else {
      targetOffset = scrollOffset + viewportSize;
    }

    this.viewport!.scrollToOffset(targetOffset, 'instant');
  }
}
